/**
 * API 클라이언트 — 백엔드 통신
 */

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8787';

const PLAYER_ID_KEY = 'tothemoon_player_id';
const PLAYER_NICKNAME_KEY = 'tothemoon_nickname';

/* ────── 플레이어 ID 관리 ────── */
export function getPlayerId(): string | null {
  return localStorage.getItem(PLAYER_ID_KEY);
}

export function getPlayerNickname(): string | null {
  return localStorage.getItem(PLAYER_NICKNAME_KEY);
}

export function setPlayerInfo(id: string, nickname: string): void {
  localStorage.setItem(PLAYER_ID_KEY, id);
  localStorage.setItem(PLAYER_NICKNAME_KEY, nickname);
}

/* ────── API 호출 ────── */
async function apiFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
  });

  const data = await res.json() as T;

  if (!res.ok) {
    throw new Error((data as { error?: string }).error || 'API Error');
  }

  return data;
}

/* ────── 닉네임 등록 ────── */
export async function registerPlayer(nickname: string): Promise<{ id: string; nickname: string }> {
  const result = await apiFetch<{ id: string; nickname: string }>('/api/players', {
    method: 'POST',
    body: JSON.stringify({ nickname }),
  });

  setPlayerInfo(result.id, result.nickname);
  return result;
}

/* ────── 점수 등록 ────── */
export interface ScoreSubmission {
  score: number;
  best_combo: number;
  max_stage: string;
  step_count: number;
  nickname?: string;
}

export interface ScoreResult {
  success: boolean;
  score_id: number;
  rank: number;
}

export async function submitScore(data: ScoreSubmission): Promise<ScoreResult | null> {
  const playerId = getPlayerId();
  if (!playerId) return null;

  try {
    return await apiFetch<ScoreResult>('/api/scores', {
      method: 'POST',
      body: JSON.stringify({
        player_id: playerId,
        ...data,
      }),
    });
  } catch (e) {
    console.warn('점수 전송 실패 (오프라인?):', e);
    return null;
  }
}

/* ────── 랭킹 조회 ────── */
export interface RankingEntry {
  rank: number;
  player_id: string;
  nickname: string;
  best_score: number;
  best_combo: number;
  max_stage: string;
  max_steps: number;
  play_count: number;
}

export interface RankingsResponse {
  rankings: RankingEntry[];
  total: number;
}

export async function getRankings(limit = 20, offset = 0): Promise<RankingsResponse> {
  return apiFetch<RankingsResponse>(`/api/rankings?limit=${limit}&offset=${offset}`);
}

/* ────── 내 순위 ────── */
export interface MyRanking {
  rank: number | null;
  best_score: number;
  best_combo: number;
  max_steps: number;
  play_count: number;
}

export async function getMyRanking(): Promise<MyRanking | null> {
  const playerId = getPlayerId();
  if (!playerId) return null;

  try {
    return await apiFetch<MyRanking>(`/api/rankings/my?player_id=${playerId}`);
  } catch {
    return null;
  }
}
